import * as React from 'react';
import { find, get } from 'lodash';
import { Accordion, AccordionGroup } from '@lendi-ui/accordion';
import Grid from '@lendi-ui/grid';
import { Heading } from '@lendi-ui/typography';

import { FIELD_PATHS, FIELD_VALUES, LIABILITY_TYPE } from '@funnels/constants';
import { useConstructionCalculator } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { FormValues, FormValuesALIEO, HomeLoanLiability, Owner, StoreApplicant } from '@funnels/types';
import { getAssetPurpose, getEntitiesWithType, getFrequencyLabel, getTotalConstructionCosts } from '@funnels/utils';

import {
  AssetCommentsReadOnlyView,
  LandValueReadOnlyView,
  LoanToValueRatioReadOnlyView,
  PropertyOwnershipReadOnlyView,
  PropertyTypeReadOnlyView,
  PropertyUseReadOnlyView,
  PropertyValueReadOnlyView,
  RentalIncomeReadOnlyView,
  TotalConstructionCostsReadOnlyView,
  TotalLoanAmountReadOnlyView,
} from '../../../Questions';
import { HomeLoanLayout } from '../_common/HomeLoan/layouts';
import { AccordionContent, AccordionWrapper, PaddedGridUnit } from '../_common/style';
import { LoanHeading } from './index.style';

interface PropertySummaryLayoutProps {
  currentValues: FormValuesALIEO;
}

const ASSET_INDEX = 0;

export function PropertySummaryLayout(props: PropertySummaryLayoutProps) {
  const { tracking } = useAnalytics();
  const { currentValues } = props;
  const { isConstruction } = useConstructionCalculator(ASSET_INDEX, currentValues as FormValues);
  const propertyValue = get(currentValues, FIELD_PATHS.ASSET_ESTIMATED_VALUE(ASSET_INDEX));
  const primaryPurpose = get(currentValues, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(ASSET_INDEX));
  const propertyType = get(currentValues, FIELD_PATHS.ASSET_PROPERTY_TYPE(ASSET_INDEX));
  const landValue = get(currentValues, FIELD_PATHS.ASSET_LAND_VALUE(ASSET_INDEX));
  const totalConstructionCosts = getTotalConstructionCosts(currentValues, ASSET_INDEX);
  const liabilities: HomeLoanLiability[] = getEntitiesWithType(currentValues, 'liabilities', LIABILITY_TYPE.HOME_LOAN);
  const [opened, setOpened] = React.useState<number[]>([]);
  const applicants = get(currentValues, FIELD_PATHS.APPLICANTS(), [] as StoreApplicant[]);
  const isRefinance = getAssetPurpose(currentValues, ASSET_INDEX) === FIELD_VALUES.REFINANCE;
  const isSecurity = getAssetPurpose(currentValues, ASSET_INDEX) === FIELD_VALUES.SECURITY_PROPERTY;
  const ownership = get(currentValues, FIELD_PATHS.ASSET_OWNERS(ASSET_INDEX), [])
    .map((owner: Owner, ownerIndex: number) => {
      const applicant = find(applicants, (applicant) => applicant.id === owner.applicantId);

      if (!applicant) {
        return null;
      }

      const { firstName = `Owner ${ownerIndex + 1}`, lastName = null } = applicant;
      let fullName = `${firstName}${lastName ? ` ${lastName}` : ''}`;
      if (applicant.trustName) fullName = applicant.trustName;
      if (applicant.companyName) fullName = applicant.companyName;
      const percent = owner.percent * 100; // 0 <= 1 in the data model.

      return `${percent}% ${fullName}`;
    })
    .filter((applicant: string | null) => applicant !== null);
  const rentalFrequency = getFrequencyLabel(get(currentValues, FIELD_PATHS.INCOME_FREQUENCY(ASSET_INDEX))) || '--';
  const rentalAmount = get(currentValues, FIELD_PATHS.INCOME_AMOUNT(ASSET_INDEX), 0);
  const rentalComments = get(currentValues, FIELD_PATHS.INCOME_COMMENTS(ASSET_INDEX));
  const totalLoanAmount =
    liabilities.reduce((total: number, liability) => {
      total += get({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_OUTSTANDING_BALANCE(0), 0);

      return total;
    }, 0) || null;
  const lvr = totalLoanAmount !== null && propertyValue ? (totalLoanAmount / propertyValue) * 100 : null;

  function handleClick(index: number) {
    const title = `Loan split ${index + 1}`;
    let arr = [...opened];

    if (arr.includes(index)) {
      arr = arr.filter((el) => el !== index);
      tracking?.accordion.collapsed({ name: title, position: title });
    } else {
      arr.push(index);
      tracking?.accordion.expanded({ name: title, position: title });
    }

    setOpened(arr);
  }

  return (
    <Grid gutter="nil">
      <PaddedGridUnit size={1 / 2}>
        <PropertyValueReadOnlyView value={propertyValue} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyUseReadOnlyView value={primaryPurpose} />
      </PaddedGridUnit>
      {isConstruction && (
        <>
          <PaddedGridUnit size={1}>
            <LandValueReadOnlyView
              title={isRefinance || isSecurity ? 'Land/Property value' : undefined}
              value={landValue}
            />
          </PaddedGridUnit>
          <PaddedGridUnit size={1}>
            <TotalConstructionCostsReadOnlyView value={totalConstructionCosts} />
          </PaddedGridUnit>
        </>
      )}
      <PaddedGridUnit size={1 / 2}>
        <PropertyOwnershipReadOnlyView value={ownership} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyTypeReadOnlyView value={propertyType} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <RentalIncomeReadOnlyView value={rentalAmount} after={` / ${rentalFrequency}`} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        {totalLoanAmount && <TotalLoanAmountReadOnlyView value={totalLoanAmount} />}
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <AssetCommentsReadOnlyView value={rentalComments} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        {!isConstruction && lvr && <LoanToValueRatioReadOnlyView value={lvr} />}
      </PaddedGridUnit>
      {liabilities.length !== 0 && (
        <AccordionWrapper>
          {liabilities.length === 1 && (
            <>
              <LoanHeading size="xxs">Loan</LoanHeading>
              <HomeLoanLayout currentValues={currentValues} liabilityIndex={0} />
            </>
          )}
          {liabilities.length > 1 && (
            <AccordionGroup>
              {liabilities.map((_, index: number) => (
                <Accordion key={index} isOpen={opened.includes(index)}>
                  <Accordion.Header onClick={() => handleClick(index)} data-component="accordion-header">
                    <Heading size="xxs">Loan split {index + 1}</Heading>
                  </Accordion.Header>
                  <Accordion.Content data-component="accordion-content">
                    <AccordionContent>
                      <HomeLoanLayout currentValues={currentValues} liabilityIndex={index} />
                    </AccordionContent>
                  </Accordion.Content>
                </Accordion>
              ))}
            </AccordionGroup>
          )}
        </AccordionWrapper>
      )}
    </Grid>
  );
}
